import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import { ControlIcons } from 'owa-control-icons';
import { isFeatureEnabled } from 'owa-feature-flags';
import loc, { format } from 'owa-localize';
import { useComputed } from 'owa-react-hooks/lib/useComputed';
import {
    END_MATCH_DELIMITER,
    START_MATCH_DELIMITER,
    lazyOnUpArrowPressedSearchInput,
    lazyOnDownArrowPressedSearchInput,
} from 'owa-search';
import { WebSearchSuggestion } from 'owa-search-service';
import {
    CLICK_ACTION_SOURCE,
    LARGE_SUGGESTION_HEIGHT,
    SUGGESTION_ID_PREFIX,
} from 'owa-search/lib/searchConstants';
import { suggestionSelected } from '../../../actions/suggestionSelected';
import { exitSearch } from '../../../actions/exitSearch';
import { onKeyDownSearchInput } from '../../../actions/onKeyDownSearchInput';
import { onEscapePressedSearchInput } from '../../../actions/onEscapePressedSearchInput';
import getScenarioStore from '../../../selectors/getScenarioStore';
import { SearchScenarioId } from '../../../store/schema/SearchScenarioId';
import getHighlightedTextForSuggestion from '../../../utils/getHighlightedTextForSuggestion';
import highlightDisplayText, { dehighlightDisplayText } from '../../../utils/highlightDisplayText';
import KeyboardCharCodes from 'owa-hotkeys/lib/utils/keyboardCharCodes';
import {
    findResultsOnBing,
    findResultsOnTheWeb,
    searchOnBing_3,
    searchOnTheWeb_3,
} from './WebSearchSuggestion.locstring.json';

const universalSuggestionStyles = require('../searchSuggestion/SearchSuggestion.scss');
const textSearchSuggestionStyles = require('../textSearchSuggestion/TextSearchSuggestion.scss');
const classNames = require('classnames/bind').bind(universalSuggestionStyles);

export interface WebSearchSuggestionProps {
    index: number;
    scenarioId: SearchScenarioId;
    suggestion: WebSearchSuggestion;
    suggestionSetTraceId: string;
    focusSearchInput: () => void;
}

const WebSearchSuggestion = observer(function WebSearchSuggestion(props: WebSearchSuggestionProps) {
    const suggestionButton = React.createRef<HTMLButtonElement>();

    /**
     * Determines whether or not this suggestion is selected.
     */
    const isSelected = useComputed((): boolean => {
        const { scenarioId, index } = props;
        const selectedSuggestionIndex = getScenarioStore(scenarioId).selectedSuggestionIndex;
        return selectedSuggestionIndex === index;
    });

    const processEvent = () => {
        const { scenarioId, suggestionSetTraceId, suggestion, index } = props;
        const { isSuggestionsCalloutClickable } = getScenarioStore(scenarioId);
        /**
         * We rely on a store value to decide if we should actually allow an
         * event to be processed. If a user double-clicks, particularly on a
         * low-end machine or a low-end browser (Edge burn), it's possible for
         * a click to happen while the SuggestionsCallout component renders
         * because it animates in slightly overlapping the input.
         */
        if (!isSuggestionsCalloutClickable) {
            return;
        }
        suggestionSelected(
            suggestion,
            CLICK_ACTION_SOURCE,
            scenarioId,
            suggestionSetTraceId,
            index
        );
    };

    /**
     * Custom selection handler for the WebSearchSuggestion because WebSearch behavior
     * poses a unique problem; unlike other Suggestion types, selecting a WebSearchSuggestion
     * takes the user to a new browser tab.
     *
     * Wrapping the WebSearchSuggestion in an anchor tag would do the trick for inducing
     * the navigation we need, but that would be a large stylistic departure from other suggestion types.
     * We also can't put special accommodations in the parent-level Suggestion component and extend
     * that component like the other suggestion types do, because an event-handler attempting
     * to hijack navigation from a nested DOM structure will get blocked by popup-blockers.
     *
     * Thus, creating the suggestion's DOM content here in the WebSearchSuggestion component itself and
     * putting a handler on it (at a top-level) affords us a solution to both with minimal changes
     * to existing patterns.
     *
     * @param searchText Text for the web search we want to perform. Includes any/all Pill content, plus user-typed query text.
     * @param bingFormCode The flight-specific form code we want to include in the web search for tracking purposes.
     */
    const handleOnSelect = (searchText: string, bingFormCode: string) => e => {
        const { scenarioId } = props;
        e.preventDefault();
        e.stopPropagation();

        processEvent();
        window.open(
            `https://www.bing.com/search?q=${encodeURIComponent(searchText)}&form=${bingFormCode}`,
            '_blank'
        );
        exitSearch('SearchBoxSuggestionDropDown', scenarioId);
    };

    /**
     * Listen for the suggestion to be selected, then set
     * the focus to the suggestion container so that the
     * keyDown and click events are processed by this
     * component instead of the SearchInput component.
     */
    React.useEffect(() => {
        if (suggestionButton.current && isSelected.get()) {
            suggestionButton.current.focus();
        }
    });

    /**
     * Once focus is shifted to this component (see the useEffect
     * above), the onKeyDown events will be scoped here rather
     * than SearchInput component as is usually the case.
     *
     * Until focus is shifted back to the SearchInput component,
     * we need to listen for and process these events here.
     */
    const handleOnKeyDown = (searchText: string, bingFormCode: string) => async e => {
        e.stopPropagation();
        const keyCode = e.keyCode;
        const { scenarioId, focusSearchInput } = props;
        // Dispatch action to modify store based on key down.
        onKeyDownSearchInput(keyCode, props.scenarioId);
        switch (keyCode) {
            case KeyboardCharCodes.Enter:
                handleOnSelect(searchText, bingFormCode);
                break;
            case KeyboardCharCodes.Up_arrow:
                // Prevents cursor from going to beginning of input.
                e.preventDefault();
                const onUpArrowPressedSearchInput = await lazyOnUpArrowPressedSearchInput.import();
                onUpArrowPressedSearchInput(scenarioId);
                focusSearchInput();
                break;
            case KeyboardCharCodes.Down_arrow:
                const onDownArrowPressedSearchInput = await lazyOnDownArrowPressedSearchInput.import();
                onDownArrowPressedSearchInput(scenarioId);
                focusSearchInput();
                break;
            case KeyboardCharCodes.Esc: {
                // Prevents IE/Edge behavior of putting last text back into the input.
                e.preventDefault();
                onEscapePressedSearchInput(scenarioId);
                break;
            }
            default:
                focusSearchInput();
                break;
        }
    };

    const getBingFormCode = (): string => {
        if (isFeatureEnabled('sea-webSearchSuggestion-webQuery')) {
            return 'OUTLS1';
        } else if (isFeatureEnabled('sea-webSearchSuggestion-bingQuery')) {
            return 'OUTLS2';
        } else if (isFeatureEnabled('sea-webSearchSuggestion-webResults')) {
            return 'OUTLS3';
        } else if (isFeatureEnabled('sea-webSearchSuggestion-bingResults')) {
            return 'OUTLS4';
        }

        return '';
    };

    const getLocalizedSuggestionText = (searchText: string, bingFormCode: string): string => {
        if (bingFormCode === 'OUTLS1') {
            return format(
                loc(searchOnTheWeb_3),
                START_MATCH_DELIMITER,
                searchText,
                END_MATCH_DELIMITER
            );
        } else if (bingFormCode === 'OUTLS2') {
            return format(
                loc(searchOnBing_3),
                START_MATCH_DELIMITER,
                searchText,
                END_MATCH_DELIMITER
            );
        } else if (bingFormCode === 'OUTLS3') {
            return loc(findResultsOnTheWeb);
        } else if (bingFormCode === 'OUTLS4') {
            return loc(findResultsOnBing);
        }

        return '';
    };

    const { index, suggestion } = props;
    const bingFormCode = getBingFormCode();

    const adjustedSearchText = suggestion.QueryText.trim();

    /**
     * Get flight-specific suggestion content.
     */
    const locText = getLocalizedSuggestionText(adjustedSearchText, bingFormCode);
    const highlightedTextRegions = highlightDisplayText(locText);
    const formattedSuggestionText = getHighlightedTextForSuggestion(highlightedTextRegions);

    const classes = classNames(
        universalSuggestionStyles.suggestionContainer,
        isSelected.get() && universalSuggestionStyles.selected,
        universalSuggestionStyles.webSearchSuggestionContainer
    );

    const style = {
        height: LARGE_SUGGESTION_HEIGHT,
        display: 'inline',
    };

    return (
        bingFormCode !== '' && (
            <button
                ref={suggestionButton}
                aria-label={dehighlightDisplayText(locText)}
                aria-selected={isSelected.get()}
                className={classes}
                id={`${SUGGESTION_ID_PREFIX}${index}`}
                role="option"
                style={style}
                onKeyDown={handleOnKeyDown(adjustedSearchText, bingFormCode)}
                onClick={handleOnSelect(adjustedSearchText, bingFormCode)}>
                <div
                    className={`${universalSuggestionStyles.contentContainer}, ${textSearchSuggestionStyles.container}`}
                    style={{ maxWidth: '95%' }}>
                    <Icon
                        className={universalSuggestionStyles.icon}
                        iconName={
                            bingFormCode === 'OUTLS1' || bingFormCode === 'OUTLS3'
                                ? ControlIcons.Search
                                : ControlIcons.BingLogo
                        }
                    />
                    <div className={universalSuggestionStyles.webSearchSuggestionText}>
                        {formattedSuggestionText}
                    </div>
                    <Icon
                        className={classNames(
                            universalSuggestionStyles.icon,
                            universalSuggestionStyles.webSearchNewTabIcon
                        )}
                        iconName={ControlIcons.OpenInNewTab}
                    />
                </div>
            </button>
        )
    );
});
export default WebSearchSuggestion;
